import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { catchError, map, of, switchMap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { FileUploadComponent } from 'src/app/components/shared/file-upload/file-upload.component';
import { FileReturn } from 'src/app/components/shared/file-upload/file-upload.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { TableComponent } from 'src/app/components/shared/table/components/table/table.component';
import {
  CONTINUE_FLAG_MAPPING,
  TrackingNumberItem,
  UNIT_Of_MEASURE,
} from 'src/app/components/shared/table/outbound-scan-table/outbound-scan-expanded-row/outbound-scan-expanded-row.constant';
import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import {
  AddScanToULDRequest,
  OutboundScan,
} from 'src/app/core/models/position-operations/outbound-gle.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { OutboundGLEService } from 'src/app/core/services/position-operations/outbound-gle/outbound-gle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  ACTION_LIST,
  COLUMN_CONFIG,
  ColumnIds,
  CONST,
  FORM_Ids,
  TableData,
} from './outbound-scan-gle-modal.constants';

@Component({
  selector: 'app-outbound-scan-gle-modal',
  templateUrl: './outbound-scan-gle-modal.component.html',
  styleUrl: './outbound-scan-gle-modal.component.scss',
  standalone: false,
})
export class OutboundScanGleModalComponent implements OnInit {
  @ViewChild('childTable') childTable!: TableComponent; // Reference to the child component

  public readonly _data = inject(MAT_DIALOG_DATA);
  private readonly _dialog = inject(MatDialog);
  public readonly commonUtils = inject(CommonUtils);
  private readonly userService = inject(UserService);
  private readonly destroy = inject(DestroyRef);
  private readonly _outboundGLEService = inject(OutboundGLEService);
  private readonly _dialogRef = inject(
    MatDialogRef<OutboundScanGleModalComponent>,
  );
  private readonly _locationService = inject(LocationService);
  private readonly fb: FormBuilder = inject(FormBuilder);

  readonly CONST = {
    ...CONST,
  };

  readonly SRC = IMAGE_SRC;
  readonly IDS = FORM_Ids;
  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly ACTION_LIST = ACTION_LIST;
  readonly UNITS = UNIT_Of_MEASURE;
  readonly SpecialCharacters = SpecialCharacters;
  readonly COLUMN_CONFIG = COLUMN_CONFIG;
  readonly displayedColumn = Object.values(ColumnIds);

  minDate!: Date;
  maxDate!: Date;
  uldForm!: FormGroup;
  tableData: TableData[] = [];
  trackingNumberData: TrackingNumberItem[] = [];
  looseCount: number = 0;
  uldCount: number = 0;
  username: string = '';
  isFileUpload: boolean = false;
  isInternationalLoad = false;
  currLocation!: CurrentLocation;

  ngOnInit(): void {
    this.initForm();
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.username = user.loginId;
        }),
      )
      .subscribe();

    this._locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((location) => {
          this.currLocation = location;
        }),
      )
      .subscribe();
  }

  /**
   * Used to define form
   *
   * @memberof AddEditUldModalComponent
   */
  initForm() {
    // If an Outbound Scan GLE item
    if (this._data.outboundScan) {
      // Add/remove package for existing ULD
      if (this._data.equipment) {
        const equipment: OutboundScan = this._data.equipment;
        this.setupForm(equipment);
      }
      // Add/remove package for a new ULD
      else if (this._data.draftRequestBody) {
        const draftBodyScan: OutboundScan = this._data.draftRequestBody;
        this.setupForm(draftBodyScan);
      }
    }
    // If a Carry over Load item
    else {
      const activeCarryOverLoad = this._data?.activeCarryOverLoad;
      this.looseCount = activeCarryOverLoad?.pkgCount ?? 0;
      this.uldCount = activeCarryOverLoad?.uldCount ?? 0;
      this.uldForm = this.fb.group({
        [FORM_Ids.SORT_DATE]: [activeCarryOverLoad.sortDate],
        [FORM_Ids.SORT]: [activeCarryOverLoad?.origSort],
        [FORM_Ids.LOAD_NAME]: [activeCarryOverLoad?.loadKey],
        [FORM_Ids.LOAD_CHART]: [activeCarryOverLoad?.loadChart],
        [FORM_Ids.EQUIPMENT_NUMBER]: [activeCarryOverLoad?.uldNumber],
        [FORM_Ids.MOVEMENT]: [this._data?.movement],
        [FORM_Ids.SEAL_CONTROL]: [this._data?.sealNumber],
        [FORM_Ids.ACTION]: [ACTION_LIST[0].value],
        [FORM_Ids.HAZMAT]: [false],
        [FORM_Ids.DRY_ICE]: [false],
        [FORM_Ids.TRACKING_NUMBERS]: [
          null,
          [Validators.required, Validators.maxLength(18)],
        ],
      });
    }
  }

  setupForm(data: any) {
    const qualifier =
      this._data?.draftRequestBody?.loadKey?.origin?.qualifier ?? '';
    const isQualifierIncluded = data?.sort?.length == 2;
    const sort = isQualifierIncluded
      ? `${data.sort}`
      : `${data.sort}${qualifier}`;
    const movement = data?.rawUlo?.loadKey?.movement?.mvmtNum;
    this.isInternationalLoad = movement && data?.sealNumber;
    this.uldForm = this.fb.group({
      [FORM_Ids.SORT_DATE]: [data.createSortDate.split(',')[0]],
      [FORM_Ids.SORT]: [sort],
      [FORM_Ids.LOAD_NAME]: [data.loadName],
      [FORM_Ids.LOAD_CHART]: [data.loadChart],
      [FORM_Ids.EQUIPMENT_NUMBER]: [data.equipmentNumber],
      [FORM_Ids.MOVEMENT]: [data.movement || movement],
      [FORM_Ids.SEAL_CONTROL]: [data.sealNumber],
      [FORM_Ids.ACTION]: [ACTION_LIST[0].value],
      [FORM_Ids.TYPE]: [null],
      [FORM_Ids.HAZMAT]: [false],
      [FORM_Ids.DRY_ICE]: [false],
      [FORM_Ids.DRY_ICE_AMOUNT_ONE]: [null],
      [FORM_Ids.DRY_ICE_AMOUNT_TWO]: [''],
      [FORM_Ids.UNIT_OF_MEASURE]: [''],
      [FORM_Ids.TRACKING_NUMBERS]: [
        null,
        [Validators.required, Validators.maxLength(18)],
      ],
    });
    this.looseCount = data.losseCount ?? 0;
    this.uldCount = data.uldCount ?? 0;
  }

  onClose() {
    this._dialogRef.close();
  }

  closePopup() {
    this._dialogRef.close('closing');
  }

  /**
   * submit form data
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  submit() {
    // When request is coming from Outbound Scan GLE
    if (this._data.outboundScan) {
      this.submitForOutboundScan();
    }

    // When request id from Carry Over Loads
    if (this._data.carryOverLoad) {
      this.submitForCarryOverLoad();
    }
  }

  submitForOutboundScan() {
    this.commonUtils.updateLoadingModal(true);
    this._outboundGLEService
      .addScanToULD(this.createRequest())
      .pipe(
        takeUntilDestroyed(this.destroy),
        switchMap((data) => {
          return of(data);
        }),
        catchError((error) => {
          if (
            error.error.code === 400 &&
            (error.error.message as string)
              ?.toLowerCase()
              ?.includes('do not load')
          ) {
            this.commonUtils.updateBanner(false);
            this.openDialogForCrossborder(error.error.message);
          } else if (error.error.code === 400) {
            return this.handleBadRequest(error, {
              ...this.createRequest(),
              events: [
                {
                  ...this.createRequest().events[0],
                  inspectEvents: error.error.response?.inspectEvents,
                },
              ],
            });
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.commonUtils.updateLoadingModal(false);
        if (response?.code === 200) {
          this.updateOnSucess(response);
        }
      });
  }

  openDialogForCrossborder(message: string) {
    const match = message.match(/[,\.]/);
    let firstPart = message;
    let restMessage = '';

    if (match) {
      const splitIndex = match.index!;
      firstPart = message.slice(0, splitIndex).trim(); // For "Do Not Load"
      restMessage = message.slice(splitIndex + 1).trim(); // For everything after "Do Not Load"
    }

    this.commonUtils.openWarningModal({
      header: this.CONST.WARNING.ALERT_HEADER,
      body: `${firstPart} </br> ${restMessage}`,
      confirmButtonText: this.CONST.WARNING.OK_BUTTON,
      rejectButtonText: '',
      hasOkButton: true,
    });
  }

  createRequest(): AddScanToULDRequest {
    let selectedLoad;
    if (this._data?.draftRequestBody && this._data.dialogForNewUlo) {
      selectedLoad = this._data?.draftRequestBody;
    } else {
      selectedLoad = this._data?.equipment?.rawUlo;
      if (selectedLoad) {
        const originSortCode = selectedLoad.loadKey.origin.sortCode;
        const destinationSortCode = selectedLoad.loadKey.destination.sortCode;
        const processingSortCode = selectedLoad.loadKey.processing.sortCode;

        selectedLoad.loadKey.origin.sortCode = this.mapSortCode(originSortCode);
        selectedLoad.loadKey.destination.sortCode =
          this.mapSortCode(destinationSortCode);
        selectedLoad.loadKey.processing.sortCode =
          this.mapSortCode(processingSortCode);
      }
    }

    const request: AddScanToULDRequest = {
      events: [
        {
          action: this.uldForm.get(FORM_Ids.ACTION)?.value, // decides if the uld is removed or added
          inspectEvents: {
            inspectSec1: false,
            inspectSec2: false,
            inspectUld: false,
            inspectSecDes: false,
            inspectCv: false,
            statusCheck: false,
            sortDateOffPlan: false,
            sortCodeOffPlan: false,
            loadChartOffPlan: false,
            notOnFile: false,
          },
          inspectContinueEvents: {
            inspectSec1UserContinueLoad: false,
            inspectSec2UserContinueLoad: false,
            inspectUldUserContinueLoad: false,
            inspectSecDesUserContinueLoad: false,
            inspectCvUserContinueLoad: false,
            statusCheckUserContinue: false,
            localInterceptUserContinue: false,
            sortDateOffPlanContinue: false,
            sortCodeOffPlanContinue: false,
            loadChartOffPlanContinue: false,
            notOnFileContinue: false,
          },
          trackingNumber: this.uldForm.get('trackingNumbers')?.value,
          hazmat: this.uldForm.get('hazmat')?.value,
          dryIce: this.uldForm.get('dryIce')?.value,
          dryIceAmount: Number(
            `${this.uldForm.get('dryIceAmountOne')?.value || '0'}.${this.uldForm.get('dryIceAmountTwo')?.value || '0'}`,
          ),
          unitOfMeasure: this.uldForm.get('unitOfMeasure')?.value,
        },
      ],
      ulo: {
        sortDate: selectedLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.loadKey.discriminator ?? '',
          origin: selectedLoad?.loadKey.origin,
          destination: selectedLoad?.loadKey.destination,
          processing: selectedLoad?.loadKey.processing,
          uld: selectedLoad?.loadKey.uld,
          service: selectedLoad?.loadKey.service,
          packageLocationRole: selectedLoad?.loadKey.packageLocationRole,
          disposition: selectedLoad?.loadKey.disposition,
          scanType: selectedLoad?.loadKey.scanType,
          loadChart: selectedLoad?.loadKey.loadChart,
          movement: selectedLoad?.loadKey.movement,
          dataCapture: selectedLoad?.loadKey.dataCapture,
          usps: selectedLoad?.loadKey.usps,
          carryOver: selectedLoad?.loadKey.carryOver,
          bulk: selectedLoad?.loadKey.bulk,
          loadType: selectedLoad?.loadKey.loadType,
          loadDescription: selectedLoad?.loadKey.loadDescription,
          flowType: selectedLoad?.loadKey.flowType,
          loadName: selectedLoad?.loadKey.loadName,
          // position: selectedLoad?.loadKey.position,
          upsPremier: selectedLoad?.loadKey.upsPremier,
        },
        userName: this.username,
      },
      fileUpload: false,
    };
    return request;
  }

  submitForCarryOverLoad() {
    this.commonUtils.updateLoadingModal(true);
    this._outboundGLEService
      .addScanToULD(this.createRequestForCarryOverLoad())
      .pipe(
        takeUntilDestroyed(this.destroy),
        switchMap((data) => {
          return of(data);
        }),
        catchError((error) => {
          if (
            error.error.code === 400 &&
            (error.error.message as string)
              ?.toLowerCase()
              ?.includes('do not load')
          ) {
            this.commonUtils.updateBanner(false);
            this.openDialogForCrossborder(error.error.message);
          } else if (error.error.code === 400) {
            return this.handleBadRequest(error, {
              ...this.createRequestForCarryOverLoad(),
              events: [
                {
                  ...this.createRequestForCarryOverLoad().events[0],
                  inspectEvents: error.error.response?.inspectEvents,
                },
              ],
            });
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.commonUtils.updateLoadingModal(false);
        if (response?.code == 200) {
          this.updateOnSucess(response);
        }
      });
  }

  createRequestForCarryOverLoad(): AddScanToULDRequest {
    const selectedLoad = this._data?.activeCarryOverLoad?.loadKeyresponse;

    const request: AddScanToULDRequest = {
      events: [
        {
          action: this.uldForm.get(FORM_Ids.ACTION)?.value, // decides if the uld is removed or added
          inspectEvents: {
            inspectSec1: false,
            inspectSec2: false,
            inspectUld: false,
            inspectSecDes: false,
            inspectCv: false,
            statusCheck: false,
            sortDateOffPlan: false,
            sortCodeOffPlan: false,
            loadChartOffPlan: false,
            notOnFile: false,
          },
          inspectContinueEvents: {
            inspectSec1UserContinueLoad: false,
            inspectSec2UserContinueLoad: false,
            inspectUldUserContinueLoad: false,
            inspectSecDesUserContinueLoad: false,
            inspectCvUserContinueLoad: false,
            statusCheckUserContinue: false,
            localInterceptUserContinue: false,
            sortDateOffPlanContinue: false,
            sortCodeOffPlanContinue: false,
            loadChartOffPlanContinue: false,
            notOnFileContinue: false,
          },
          trackingNumber: this.uldForm.get('trackingNumbers')?.value,
        },
      ],
      ulo: {
        sortDate: this._data?.activeCarryOverLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.discriminator ?? '',
          origin: selectedLoad?.origin,
          destination: selectedLoad?.destination,
          processing: selectedLoad?.processing,
          uld: selectedLoad?.uld,
          service: selectedLoad?.service,
          packageLocationRole: selectedLoad?.packageLocationRole,
          disposition: selectedLoad?.disposition,
          scanType: selectedLoad?.scanType,
          loadChart: selectedLoad?.loadChart,
          movement: selectedLoad?.movement,
          dataCapture: selectedLoad?.dataCapture,
          usps: selectedLoad?.usps,
          carryOver: selectedLoad?.carryOver,
          bulk: selectedLoad?.bulk,
          loadType: selectedLoad?.loadType,
          loadDescription: selectedLoad?.loadDescription,
          flowType: selectedLoad?.flowType,
          loadName: selectedLoad?.loadName,
          upsPremier: selectedLoad?.upsPremier,
        },
        userName: this.username,
      },
      fileUpload: false,
    };
    return request;
  }

  handleBadRequest(data: any, payload: AddScanToULDRequest) {
    const inspectEvents = data.error.response?.pkgList?.[0]?.inspectEvents;
    const message = data.error.response?.pkgList?.[0]?.message;
    const hazmatMessage =
      data.error.response?.pkgList?.[0]?.hazmatResponse?.message;

    if (inspectEvents) {
      const flags = Object.keys(inspectEvents).filter(
        (key) => inspectEvents[key],
      );

      if (flags.length > 0 && message) {
        const updatedPayload: AddScanToULDRequest = {
          ...payload,
          events: [
            {
              ...payload.events[0],
              inspectEvents: { ...inspectEvents },
            },
          ],
        };
        this.commonUtils.updateBanner(false);
        this.openDialog(flags, updatedPayload, message);
      } else if (flags.length === 0 && hazmatMessage) {
        this.commonUtils.updateBannerType400Error(hazmatMessage);
      } else if (flags.length === 0 && message) {
        this.commonUtils.updateBannerType400Error(message);
      } else {
        this.commonUtils.updateBannerType400Error(
          data.error?.errors.length > 0
            ? data.error?.errors[0]?.message
            : data.error?.message,
        );
      }
    } else {
      this.commonUtils.updateBannerType400Error(
        data.error?.errors.length > 0
          ? data.error?.errors[0]?.message
          : data.error?.message,
      );
    }

    return of(data);
  }

  openDialog(flags: string[], payload: AddScanToULDRequest, message: string) {
    this.commonUtils
      .openWarningModal({
        header: this.CONST.WARNING.HEADER,
        body: message,
        confirmButtonText: this.CONST.WARNING.CONFIRM_BUTTON,
        rejectButtonText: this.CONST.WARNING.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          const updatedPayload = { ...payload };
          flags.forEach((flag) => {
            const continueFlag = CONTINUE_FLAG_MAPPING[flag];
            updatedPayload.events.forEach((events) => {
              if (events.inspectContinueEvents) {
                (events.inspectContinueEvents as Record<string, boolean>)[
                  continueFlag
                ] = true;
              }
            });
          });
          this.commonUtils.updateLoadingModal(true);
          return this._outboundGLEService
            .addScanToULD(updatedPayload)
            .pipe(
              catchError((error) => {
                if (error?.error?.code === 400) {
                  return this.handleBadRequest(error, updatedPayload);
                }
                return of(error.error.response);
              }),
            )
            .subscribe((response) => {
              this.commonUtils.updateLoadingModal(false);
              if (response?.code === 200) {
                this.updateOnSucess(response);
              }
            });
        } else {
          return of(null);
        }
      });
  }

  updateOnSucess(response: any) {
    this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
    this.isFileUpload = false;

    this.uldCount = Number(response.response.uldCount ?? 0);
    this.looseCount = Number(response.response.looseCount ?? 0);
    this.updateTrackingNumberFields(response);
  }

  createRequestForFile(trackingNumbers: string[]) {
    let selectedLoad;
    if (this._data?.draftRequestBody && this._data.dialogForNewUlo) {
      selectedLoad = this._data?.draftRequestBody;
    } else {
      selectedLoad = this._data?.equipment?.rawUlo;
      if (selectedLoad) {
        // Map sort codes
        const originSortCode = selectedLoad.loadKey.origin.sortCode;
        const destinationSortCode = selectedLoad.loadKey.destination.sortCode;
        const processingSortCode = selectedLoad.loadKey.processing.sortCode;

        selectedLoad.loadKey.origin.sortCode = this.mapSortCode(originSortCode);
        selectedLoad.loadKey.destination.sortCode =
          this.mapSortCode(destinationSortCode);
        selectedLoad.loadKey.processing.sortCode =
          this.mapSortCode(processingSortCode);
      }
    }

    const baseEvent = {
      action: this.uldForm.get(FORM_Ids.ACTION)?.value,
      inspectEvents: {
        inspectSec1: false,
        inspectSec2: false,
        inspectUld: false,
        inspectSecDes: false,
        inspectCv: false,
        statusCheck: false,
        sortDateOffPlan: false,
        sortCodeOffPlan: false,
        loadChartOffPlan: false,
        notOnFile: false,
      },
      inspectContinueEvents: {
        inspectSec1UserContinueLoad: false,
        inspectSec2UserContinueLoad: false,
        inspectUldUserContinueLoad: false,
        inspectSecDesUserContinueLoad: false,
        inspectCvUserContinueLoad: false,
        statusCheckUserContinue: false,
        localInterceptUserContinue: false,
        sortDateOffPlanContinue: false,
        sortCodeOffPlanContinue: false,
        loadChartOffPlanContinue: false,
        notOnFileContinue: false,
      },
      hazmat: false,
      dryIce: false,
    };

    const events = trackingNumbers.map((trackNo: any) => ({
      ...baseEvent,
      trackingNumber: trackNo,
    }));

    const request: AddScanToULDRequest = {
      events,
      ulo: {
        sortDate: selectedLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.loadKey.discriminator ?? '',
          origin: selectedLoad?.loadKey.origin,
          destination: selectedLoad?.loadKey.destination,
          processing: selectedLoad?.loadKey.processing,
          uld: selectedLoad?.loadKey.uld,
          service: selectedLoad?.loadKey.service,
          packageLocationRole: selectedLoad?.loadKey.packageLocationRole,
          disposition: selectedLoad?.loadKey.disposition,
          scanType: selectedLoad?.loadKey.scanType,
          loadChart: selectedLoad?.loadKey.loadChart,
          movement: selectedLoad?.loadKey.movement,
          dataCapture: selectedLoad?.loadKey.dataCapture,
          usps: selectedLoad?.loadKey.usps,
          carryOver: selectedLoad?.loadKey.carryOver,
          bulk: selectedLoad?.loadKey.bulk,
          loadType: selectedLoad?.loadKey.loadType,
          loadDescription: selectedLoad?.loadKey.loadDescription,
          flowType: selectedLoad?.loadKey.flowType,
          loadName: selectedLoad?.loadKey.loadName,
          upsPremier: selectedLoad?.loadKey.upsPremier,
        },
        userName: this.username,
      },
      fileUpload: true,
    };
    return request;
  }

  createRequestForCarryOverLoadFile(trackingNumbers: string[]) {
    const selectedLoad = this._data?.activeCarryOverLoad?.loadKeyresponse;

    const baseEvent = {
      action: this.uldForm.get(FORM_Ids.ACTION)?.value,
      inspectEvents: {
        inspectSec1: false,
        inspectSec2: false,
        inspectUld: false,
        inspectSecDes: false,
        inspectCv: false,
        statusCheck: false,
        sortDateOffPlan: false,
        sortCodeOffPlan: false,
        loadChartOffPlan: false,
        notOnFile: false,
      },
      inspectContinueEvents: {
        inspectSec1UserContinueLoad: false,
        inspectSec2UserContinueLoad: false,
        inspectUldUserContinueLoad: false,
        inspectSecDesUserContinueLoad: false,
        inspectCvUserContinueLoad: false,
        statusCheckUserContinue: false,
        localInterceptUserContinue: false,
        sortDateOffPlanContinue: false,
        sortCodeOffPlanContinue: false,
        loadChartOffPlanContinue: false,
        notOnFileContinue: false,
      },
      hazmat: false,
      dryIce: false,
    };

    const events = trackingNumbers.map((trackNo) => ({
      ...baseEvent,
      trackingNumber: trackNo,
    }));

    const request: AddScanToULDRequest = {
      events,
      ulo: {
        sortDate: this._data?.activeCarryOverLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.discriminator ?? '',
          origin: selectedLoad?.origin,
          destination: selectedLoad?.destination,
          processing: selectedLoad?.processing,
          uld: selectedLoad?.uld,
          service: selectedLoad?.service,
          packageLocationRole: selectedLoad?.packageLocationRole,
          disposition: selectedLoad?.disposition,
          scanType: selectedLoad?.scanType,
          loadChart: selectedLoad?.loadChart,
          movement: selectedLoad?.movement,
          dataCapture: selectedLoad?.dataCapture,
          usps: selectedLoad?.usps,
          carryOver: selectedLoad?.carryOver,
          bulk: selectedLoad?.bulk,
          loadType: selectedLoad?.loadType,
          loadDescription: selectedLoad?.loadDescription,
          flowType: selectedLoad?.flowType,
          loadName: selectedLoad?.loadName,
          upsPremier: selectedLoad?.upsPremier,
        },
        userName: this.username,
      },
      fileUpload: true,
    };

    return request;
  }

  mapSortCode(sortCode: string): string {
    if (/^\d+$/.test(sortCode)) return sortCode;

    if (sortCode === 'S') return '01';
    if (sortCode === 'M') return '02';
    if (sortCode === 'D') return '03';
    if (sortCode === 'T') return '04';
    if (sortCode === 'N') return '05';
    if (sortCode === 'P') return '06';
    if (sortCode === 'L') return '07';
    if (sortCode === 'A') return '08';
    return '00';
  }

  updateTrackingNumberFields(response: any) {
    const pkg = response?.response?.pkgList?.[0];

    if (Array.isArray(pkg?.status) && Array.isArray(pkg?.reason)) {
      const count = Math.max(pkg.status.length, pkg.reason.length);

      for (let i = 0; i < count; i++) {
        this.tableData.push({
          trackingNumbers: `${
            this.uldForm.get(FORM_Ids.TRACKING_NUMBERS)?.value
          } (${this.uldForm.get(FORM_Ids.ACTION)?.value})`,
          status: pkg.status[i] || '',
          reason: pkg.reason[i] || '',
          flex: pkg.flex,
        });
      }
    } else {
      this.tableData.push({
        trackingNumbers: `${
          this.uldForm.get(FORM_Ids.TRACKING_NUMBERS)?.value
        } (${this.uldForm.get(FORM_Ids.ACTION)?.value})`,
        status: pkg.status as string,
        reason: pkg.reason as string,
        flex: pkg.flex,
      });
    }
    const tableData = this.tableData;
    if (this.childTable?.dataSource) {
      this.childTable.dataSource.data = [...tableData];
    }

    this.uldForm.get(FORM_Ids.TRACKING_NUMBERS)?.reset();
    this.uldForm.get(FORM_Ids.TYPE)?.reset();
    this.uldForm.get(FORM_Ids.HAZMAT)?.reset();
    this.uldForm.get(FORM_Ids.DRY_ICE)?.reset();

    this.uldForm.updateValueAndValidity();
  }

  /**
   * Used to open file upload modal
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  fileUpload() {
    this.isFileUpload = true;
    this._dialog
      .open(FileUploadComponent, {
        disableClose: true,
        data: { trackingNumber: true },
      })
      .afterClosed()
      .subscribe((fileData: FileReturn) => {
        this.commonUtils.updateLoadingModal(true);
        const requests$ = this._data.carryOverLoad
          ? fileData.returnData.map((file) => ({
              numbers: file.map((val) => val.trackingNumber),
              call$: this._outboundGLEService
                .addScanToULD(
                  this.createRequestForCarryOverLoadFile(
                    file.map((val) => val.trackingNumber),
                  ),
                )
                .pipe(takeUntilDestroyed(this.destroy)),
            }))
          : fileData.returnData.map((file) => ({
              numbers: file.map((val) => val.trackingNumber),
              call$: this._outboundGLEService
                .addScanToULD(
                  this.createRequestForFile(
                    file.map((val) => val.trackingNumber),
                  ),
                )
                .pipe(takeUntilDestroyed(this.destroy)),
            }));

        this.commonUtils
          .runFileUploadCallsInChunks(
            requests$,
            this.commonUtils.generateUploadString(
              new Date(),
              this.currLocation.location?.locationDetails.code ?? '',
              this.username,
            ),
            fileData.fileName,
            fileData.fileSize,
            1,
          )
          .subscribe((val) => {
            if (val) {
              this.commonUtils.updateBannerType(
                BANNER_TYPE.RESULTS_FROM_UPLOAD,
                null,
                this.commonUtils.fileUploadResponse(),
              );
            }

            this._dialogRef.close('updated');
          });
      });
  }

  onHazmatChange(checked: boolean) {
    this.uldForm.get(FORM_Ids.HAZMAT)?.setValue(checked);
  }

  onDryIceChange(checked: boolean) {
    if (!checked) {
      this.uldForm.get(FORM_Ids.DRY_ICE_AMOUNT_ONE)?.disable();
      this.uldForm.get(FORM_Ids.DRY_ICE_AMOUNT_TWO)?.disable();
      this.uldForm.get(FORM_Ids.UNIT_OF_MEASURE)?.disable();
    } else {
      this.uldForm.get(FORM_Ids.DRY_ICE_AMOUNT_ONE)?.enable();
      this.uldForm.get(FORM_Ids.DRY_ICE_AMOUNT_TWO)?.enable();
      this.uldForm.get(FORM_Ids.UNIT_OF_MEASURE)?.enable();
    }
    this.uldForm.get(FORM_Ids.DRY_ICE)?.setValue(checked);
    this.uldForm.updateValueAndValidity();
  }

  get sortDate() {
    return formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
  }
}
